import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import api from '../services/api';
import CardRenderer from '../components/CardRenderer';

const SAMPLE_DATA = {
  'Professional Gradient': { name: 'Rahul Sharma', title: 'Product Designer', company: 'Surat, India', phone: '+91 99887 76655', email: 'rahul@design.co', website: 'rahulsharma.com', linkedin: 'https://linkedin.com/in/rahul' },
  'Classic Professional': { name: 'Rahul Sharma', title: 'Product Designer', company: 'Surat, India', phone: '+91 99887 76655', email: 'rahul@design.co', website: 'rahulsharma.com', linkedin: 'https://linkedin.com/in/rahul' },
  'Clean Minimal': { name: 'Priya Kumar', title: 'UX Researcher', company: 'Mumbai', phone: '+91 98765 43210', email: 'priya@studio.com', website: 'priyakumar.com', linkedin: 'https://linkedin.com/in/priya' },
  'Modern Creative': { name: 'Aditya Verma', title: 'Creative Director', company: 'Delhi', phone: '+91 98765 11111', email: 'aditya@creative.io', website: 'adityaverma.com' },
  'Minimalist': { name: 'Arjun Mehta', title: 'Business Consultant', phone: '+91 99887 76655', email: 'arjun@consulting.io', website: 'arjunmehta.com' },
  'Executive Pro': { name: 'Neha Kapoor', title: 'Director', company: 'TechCorp India', phone: '+91 88776 55443', email: 'neha@techcorp.in', website: 'nehakapoor.com' },
  'Medical Professional': { name: 'Dr. Anita Desai', title: 'Cardiologist', company: 'Heart Care Clinic', phone: '+91 98765 00001', email: 'dr.anita@heartcare.in' },
  'Healthcare Elite': { name: 'Dr. Vikram Patel', title: 'Neurologist', company: 'Apollo Hospital', phone: '+91 98765 00002', email: 'vikram@apollo.in' },
  'Creative Portfolio': { name: 'Zara Ahmed', title: 'Photographer', phone: '+91 98765 00003', email: 'zara@portfolio.com', website: 'zaraahmed.art' },
  'Freelance Modern': { name: 'Karan Singh', title: 'Full-Stack Developer', phone: '+91 98765 00004', email: 'karan@freelance.dev', website: 'karansingh.dev' },
  'Property Expert': { name: 'Meera Joshi', title: 'Senior Broker', company: 'Prime Realty', phone: '+91 98765 00005', email: 'meera@primerealty.in' },
  'Realtor Pro': { name: 'Amit Patel', title: 'Real Estate Agent', company: 'HomeFinder', phone: '+91 98765 00006', email: 'amit@homefinder.in' },
  'Campus Connect': { name: 'Riya Sharma', title: 'Computer Science', company: 'IIT Bombay', phone: '+91 98765 00007', email: 'riya@campus.edu' },
  'Sales Executive': { name: 'Rohan Gupta', title: 'VP of Sales', company: 'SalesForce India', phone: '+91 98765 00008', email: 'rohan@salesforce.in' },
  'Business Prestige': { name: 'Vikram Malhotra', title: 'Managing Director', company: 'Sterling Capital Partners', phone: '+91 98765 00009', email: 'vikram@sterlingcp.in' },
};

const TemplatePreview = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const numId = parseInt(templateId, 10);
      // Use list endpoint (reliable) then find by ID
      const { data } = await api.get('/templates');
      if (data?.success && Array.isArray(data.templates)) {
        const found = data.templates.find(t => t.id === numId || t.id?.toString() === templateId);
        if (found) {
          setTemplate({ id: found.id, name: found.name, description: found.description || '', category: found.category || 'Professional' });
          return;
        }
      }
      // Fallback to single endpoint
      try {
        const { data: single } = await api.get(`/templates/${templateId}`);
        setTemplate(single?.template || null);
        if (!single?.template) setError('Template not found');
      } catch (_) {
        setError('Template not found');
        setTemplate(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load template');
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>Loading template preview...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !template) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2, gap: 2 }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>{error || 'Template not found'}</Alert>
        <Button onClick={() => navigate('/templates')}>Back to Templates</Button>
      </Box>
    );
  }

  const templateName = template?.name || 'Template';
  const sampleData = SAMPLE_DATA[templateName] || { name: 'Your Name', title: 'Your Title', company: 'Your Company', phone: '+91 98765 43210', email: 'you@company.com' };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.100', py: 3, px: 2 }}>
      <Container maxWidth="sm" disableGutters sx={{ maxWidth: 440 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/templates')} sx={{ bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'action.hover' } }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>{templateName}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Preview — this is exactly how your card will look</Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <CardRenderer templateName={templateName} cardData={sampleData} />
        </Box>

        {template.description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{template.description}</Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => navigate('/templates')} sx={{ textTransform: 'none', fontWeight: 600 }}>Back to Templates</Button>
          <Button variant="contained" onClick={() => navigate(`/customize?template=${templateId}`)} sx={{ textTransform: 'none', fontWeight: 600 }}>Use this template</Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TemplatePreview;
